import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import { services } from "../../data/homepageData";
import { trackInitiateCheckout } from "../../lib/tracking";

// Map price strings like "$99" to numeric USD for pixel value
const priceToValue = (p) => parseInt(String(p).replace(/[^0-9]/g, ""), 10) || 0;

export const ServicesGrid = () => (
  <section id="services" className="py-20">
    <div className="mx-auto max-w-7xl px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold">
          Choose Your
          <span className="text-blue-400"> Service Level</span>
        </h2>
        <p className="mt-4 text-slate-400 max-w-2xl mx-auto">
          From free assessment to priority support — pick what fits your situation.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {services.map((service) => {
          const IconComponent = service.icon;
          const isExternal = service.ctaLink.startsWith("http");
          const ctaClasses = `w-full py-3 rounded-xl font-semibold text-center transition ${
            service.highlight
              ? "bg-blue-500 text-white hover:bg-blue-600"
              : "bg-white/10 text-white hover:bg-white/20"
          }`;

          return (
            <div
              key={service.id}
              data-testid={service.id}
              className={`rounded-2xl p-6 flex flex-col ${
                service.highlight
                  ? "border-2 border-blue-500 bg-blue-500/10 ring-1 ring-blue-500/50"
                  : "border border-white/10 bg-white/5"
              }`}
            >
              {service.highlight && (
                <span className="self-start px-3 py-1 rounded-full bg-blue-500 text-white text-xs font-semibold mb-4">
                  Best Value
                </span>
              )}

              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                service.highlight ? "bg-blue-500/30" : "bg-white/10"
              }`}>
                <IconComponent className={`w-6 h-6 ${service.highlight ? "text-blue-400" : "text-slate-300"}`} />
              </div>

              <h3 className="text-xl font-semibold mb-1">{service.title}</h3>
              <div className="flex items-baseline gap-2 mb-3">
                <span className={`text-2xl font-bold ${service.highlight ? "text-blue-400" : "text-white"}`}>
                  {service.price}
                </span>
                <span className="text-xs text-slate-500">{service.priceNote}</span>
              </div>

              <p className="text-slate-400 text-sm mb-6">{service.description}</p>

              <ul className="space-y-2 mb-6 flex-grow">
                {service.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {isExternal ? (
                <a
                  href={service.ctaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackInitiateCheckout({ tier: service.title, priceUsd: priceToValue(service.price) })}
                  className={ctaClasses}
                  data-testid={`${service.id}-cta`}
                >
                  {service.cta}
                </a>
              ) : (
                <Link
                  to={service.ctaLink}
                  className={`${ctaClasses} block`}
                  data-testid={`${service.id}-cta`}
                >
                  {service.cta}
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-10 text-center text-xs text-slate-500 max-w-2xl mx-auto">
        Individual results vary. Platform decisions are outside our control. See our{" "}
        <a href="/refund-policy" className="underline hover:text-slate-300">$200 Denial Credit policy</a>{" "}
        for details.
      </p>
    </div>
  </section>
);
